import React, { useMemo } from "react";
import { Paper, Typography, Box } from "@mui/material";
import Plot from "react-plotly.js";
import { CHART_COLORS, PROFESSIONAL_CHART_COLORS } from "../../utils/constants";
import { useResponsive } from "../../utils/responsive";
import { PieChartSkeleton } from "../common/Skeletons";
import ErrorState from "../common/ErrorState";
import useDashboardData from "../../hooks/useDashboardData";

const CategoryDonutChart = () => {
  const { data, loading, error, refetch } = useDashboardData('records/aggregate', { groupBy: 'misconduct_event_category' });
  const { isMobile, isTablet } = useResponsive();

  // Memoize chart data preparation
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { categories: [], counts: [] };
    
    return {
      categories: data.map(d => d.misconduct_event_category || d.category),
      counts: data.map(d => d.count)
    };
  }, [data]);

  return (
    <Paper 
      sx={{ 
        p: 3, 
        width: '100%', 
        minHeight: 700, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: PROFESSIONAL_CHART_COLORS.SURFACE,
        borderRadius: 2,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(96, 125, 159, 0.15)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.25)',
          transform: 'translateY(-4px)'
        }
      }}
    >
      <Typography variant="h6" gutterBottom className="chart-title" sx={{ color: PROFESSIONAL_CHART_COLORS.TEXT }}>
        Misconduct Cases by Category
      </Typography>
      <Typography variant="body2" color={PROFESSIONAL_CHART_COLORS.MUTED} gutterBottom>
        Distribution of all reported misconduct cases grouped by category.
      </Typography>
      <Box 
        sx={{ 
          width: '100%', 
          maxWidth: isMobile ? '100%' : 900, 
          height: isMobile ? 400 : 600, 
          overflowX: 'auto', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          position: 'relative',
          mt: 2
        }}
      >
        {loading ? (
          <PieChartSkeleton />
        ) : error ? (
          <ErrorState height={isMobile ? 400 : 600} message={error} onRetry={refetch} />
        ) : data.length === 0 ? (
          <ErrorState height={isMobile ? 400 : 600} message="No data available." severity="info" />
        ) : (
          <Plot
            data={[{
              values: chartData.counts,
              labels: chartData.categories,
              type: "pie",
              hole: 0.6,
              marker: { colors: CHART_COLORS },
              textinfo: isMobile ? "percent" : "label+percent",
              hoverinfo: "label+value+percent",
              textfont: { 
                size: isMobile ? 10 : 14,
                color: PROFESSIONAL_CHART_COLORS.TEXT
              },
              hoverlabel: {
                bgcolor: PROFESSIONAL_CHART_COLORS.SURFACE,
                bordercolor: PROFESSIONAL_CHART_COLORS.PRIMARY,
                font: { color: PROFESSIONAL_CHART_COLORS.TEXT }
              }
            }]}
            layout={{
              autosize: true,
              width: isMobile ? 350 : isTablet ? 650 : 850,
              height: isMobile ? 400 : 600,
              showlegend: !isMobile,
              legend: isMobile ? {} : { 
                orientation: "v", 
                x: 1.02, 
                y: 0.5, 
                font: { 
                  size: isTablet ? 12 : 16,
                  color: PROFESSIONAL_CHART_COLORS.TEXT 
                },
                bgcolor: 'rgba(0,0,0,0)',
                bordercolor: 'rgba(96, 125, 159, 0.15)'
              },
              margin: isMobile 
                ? { t: 30, l: 30, r: 30, b: 30 }
                : { t: 30, l: 30, r: 300, b: 30 },
              paper_bgcolor: 'rgba(0,0,0,0)',
              plot_bgcolor: 'rgba(0,0,0,0)',
              font: {
                color: PROFESSIONAL_CHART_COLORS.TEXT
              }
            }}
            config={{ 
              responsive: true,
              displayModeBar: !isMobile,
              modeBarButtonsToRemove: ['toImage', 'sendDataToCloud'],
              displaylogo: false
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default CategoryDonutChart;